<template>
  <div class="row">

    <div class="col-12 px-0">
      <h1 class="d-flex align-items-center">
        <span class="icon mr-2 icon-settings_profil_verwalten" aria-hidden="true"></span>
        {{ $t('views.roomEdit.title') }}
      </h1>
    </div>

    <link-back to="/rooms" i18nMessage="common.backToOverview" class="mb-3" />

    <loading-container :state="state" errorLinkTo="/rooms">
      <confirmation-message
        i18nMessage="views.roomEdit.confirmDelete"
        :i18nParams="[name]"
        confirmButtonType="delete"
        v-on:canceled="showDeleteConfirmation = false"
        v-on:confirmed="deleteRoom()"
        v-if="showDeleteConfirmation"
      />
      <form @submit.prevent="onSubmit()" v-if="!showDeleteConfirmation">

        <div class="row mx-auto">
          <div class="col-12 col-md-9 px-0">
            <control-input
              i18nLabel="common.room.name"
              :validation="v$.name"
              v-model="name"
            />
            <control-input
              i18nLabel="common.room.description"
              :validation="v$.description"
              v-model="description"
            />
          </div>
          <div class="col-12 col-md-3 text-center">
            <router-link :to="{ name: 'admin-room-edit-image', params: { id: id }}">{{ $t('common.room.editImage') }}</router-link><br>
            <room-image :room="room" class="roomImageHeight mt-2" />
          </div>
        </div>

        <h2 class="h2-title mt-3">{{ $t('common.room.capacity') }}</h2>

        <div class="col-12 d-flex bg-lightblue p-3 kapazitaetrow mt-3">
          <control-integer
            i18nLabel="common.room.personCount"
            :validation="v$.capacity"
            v-model="capacity"
            :showBreak="true"
            class=" d-flex flex-wrap align-items-center mr-3"
          />
        </div>

        <h2 class="h2-title mt-3">{{ $t('common.room.reservationPeriod') }}</h2>

        <div class="col-12 d-flex align-items-start align-items-sm-center flex-column flex-sm-row bg-lightblue p-3 kapazitaetrow mt-3">
          <control-integer
            i18nLabel="common.room.openStartValue"
            :validation="v$.openStartValue"
            v-model="openStartValue"
            :showBreak="true"
            class=" d-flex flex-wrap align-items-center mr-3"
          />
          <control-select
            i18nLabel="common.room.openStartType"
            :validation="v$.openStartType"
            :labelHidden="true"
            v-model="openStartType"
            :availableValues="selectOptions()"
          />
          <span class="ml-0 ml-sm-3">{{ $t('common.room.additionalStartEndInfo') }}</span>
         </div>

        <div class="col-12 d-flex align-items-start align-items-sm-center flex-column flex-sm-row bg-lightblue p-3 kapazitaetrow mt-3">
          <control-integer
            i18nLabel="common.room.openEndValue"
            :validation="v$.openEndValue"
            v-model="openEndValue"
            :showBreak="true"
            class=" d-flex flex-wrap align-items-center mr-3"
          />
          <control-select
            i18nLabel="common.room.openEndType"
            :validation="v$.openEndType"
            :labelHidden="true"
            v-model="openEndType"
            :availableValues="selectOptions()"
          />
          <span class="ml-0 ml-sm-3">{{ $t('common.room.additionalStartEndInfo') }}</span>
         </div>

        <div class="row mx-auto mt-4">
          <div class="col-12 col-md-4 offset-md-2 col-lg-3 offset-lg-3 mt-1 px-xs-0">
          <button-cancel to="/rooms" class="btn-w-100" />
          </div>
          <div class="col-12 col-md-4 col-lg-3 mt-1 px-xs-0">
          <button-save i18nMessage="views.roomEdit.save" class="btn-w-100" />
          </div>
        </div>

        <h2 class="h2-title mt-4">{{ $t('common.room.delete') }}</h2>
        <div class="col-12 d-flex align-items-center bg-danger-teres p-3 justify-content-center">
          <feedback-message :message="$t('common.room.notDeletable', { count: reservationCount })" v-if="reservationCount !== 0" />
          <button-delete v-if="reservationCount === 0" i18nMessage="common.room.delete" v-on:clicked="showDeleteConfirmation = true" />
        </div>
      </form>
    </loading-container>
  </div>
</template>

<script lang="ts">
import { urlParamString } from '@/utils/navigationUtils'
import { defineComponent } from 'vue'
import LoadingMixin from '@/mixins/LoadingMixin'
import { useVuelidate } from '@vuelidate/core'
import { validateAndFocusInvalid } from '@/utils/formUtils'
import { createRoomValidations } from '@/utils/validationUtils'
import { parseDayPeriodTypeEnum } from '@/utils/apiUtils'

import LoadingContainer from '@/components/structure/LoadingContainer.vue'
import ControlInput from '@/components/controls/ControlInput.vue';
import { LoadingState } from '@/utils/loadingUtils'
import ButtonSave from '@/components/interaction/ButtonSave.vue'
import ButtonCancel from '@/components/interaction/ButtonCancel.vue'
import ButtonDelete from '@/components/interaction/ButtonDelete.vue'
import { adminService } from '@/services/adminService'
import { Room, DayPeriodTypeEnum } from '@/generated/openapi'
import ControlSelect from '@/components/controls/ControlSelect.vue'
import { SelectOption } from '@/models/SelectOption'
import ControlInteger from '@/components/controls/ControlInteger.vue'
import ConfirmationMessage from '@/components/feedback/ConfirmationMessage.vue'
import config from '@/config/config'
import FeedbackMessage from '@/components/feedback/FeedbackMessage.vue'
import LinkBack from '@/components/interaction/LinkBack.vue'
import RoomImage from '@/components/info/RoomImage.vue';
import { InfoPopupBase, InfoPopupType } from '@/models/InfoPopupData'

export default defineComponent({
  name: 'RoomEdit',
  mixins: [
    LoadingMixin
  ],
  components: {
    LoadingContainer,
    ControlInput,
    ButtonSave,
    ButtonCancel,
    ButtonDelete,
    ControlSelect,
    ControlInteger,
    ConfirmationMessage,
    FeedbackMessage,
    LinkBack,
    RoomImage
  },
  setup() {
    return {
      v$: useVuelidate()
    }
  },
  data() {
    return {
      id: '',
      version: -1,
      name: '',
      description: '',
      capacity: 0,
      openStartType: DayPeriodTypeEnum.Day as string,
      openStartValue: config.timeslotBookableStartValue,
      openEndType: DayPeriodTypeEnum.Day as string,
      openEndValue: config.timeslotBookableEndValue,
      showDeleteConfirmation: false,
      reservationCount: 0,
      room: null as Room|null
    }
  },
  created() {
    const roomId = urlParamString(this.$route, 'id')
    Promise.all([
      adminService.getRoom(roomId),
      adminService.countActiveTickets(roomId)
    ])
    .then(([room, reservationCount]) => {
      this.room = room
      this.id = room.id
      this.version = room.version
      this.name = room.name || ''
      this.description = room.description || ''
      this.capacity = room.capacity || 0,
      this.openStartType = room.openStart?.type || DayPeriodTypeEnum.Day
      this.openStartValue = room.openStart?.value || 2
      this.openEndType = room.openEnd?.type || DayPeriodTypeEnum.WorkingDay
      this.openEndValue = room.openEnd?.value || 30
      this.reservationCount = reservationCount
      this.state = LoadingState.success
    }).catch(this.handleError)
  },
  validations() {
    return createRoomValidations()
  },
  methods: {
    selectOptions(): SelectOption[] {
      return [
        DayPeriodTypeEnum.WorkingDay,
        DayPeriodTypeEnum.Day
      ].map(dayType => ({
        value: dayType,
        label: this.$t('common.dayTypes.' + dayType)
      }))
    },
    asRoom(): Room {
      return {
        id: this.id,
        version: this.version,
        name: this.name,
        description: this.description,
        capacity: this.capacity,
        openStart: {
          type: parseDayPeriodTypeEnum(this.openStartType),
          value: this.openStartValue
        },
        openEnd: {
          type: parseDayPeriodTypeEnum(this.openEndType),
          value: this.openEndValue
        }
      }
    },
    onSubmit() {
      validateAndFocusInvalid(this.v$, this.$el).then(this.updateRoom)
    },
    updateRoom() {
      this.state = LoadingState.loading;
      adminService.updateRoom(this.asRoom()).then(() => {
        this.$store.commit('addInfoPopup', {
          title: this.$t('views.roomEdit.infopopup.edit'),
          type: InfoPopupType.info
        } as InfoPopupBase)
        this.$router.push('/rooms')
      }).catch(this.handleError)
    },
    deleteRoom() {
      this.state = LoadingState.loading;
      adminService.deleteRoom(this.id).then(() => {
        this.$store.commit('addInfoPopup', {
          title: this.$t('views.roomEdit.infopopup.delete'),
          type: InfoPopupType.info
        } as InfoPopupBase)
        this.$router.push('/rooms')
      }).catch(this.handleError)
    }
  }
});
</script>
